import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, MapPin, Music, Ticket, Users, PlusCircle } from "lucide-react";
import { toast } from "sonner";

function formatWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch { return iso; }
}

export default function Events() {
  const { user } = useAuth();
  const [concerts, setConcerts] = useState([]);
  const [featuredBands, setFeaturedBands] = useState([]);
  const [isBandOwner, setIsBandOwner] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [ev, bands, mine] = await Promise.all([
          api.get("/concerts?upcoming_only=true&limit=60"),
          api.get("/artists/featured"),
          user ? api.get("/artists/mine").catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
        ]);
        if (!alive) return;
        setConcerts(ev.data?.items || []);
        setFeaturedBands(bands.data?.items || []);
        setIsBandOwner(!!mine.data?.artist);
      } catch (e) {
        if (alive) toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [user]);

  return (
    <div className="min-h-[100vh]" data-testid="events-page">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-[#EAE3DE] bg-[#1B1614] text-white">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            backgroundImage: "url('https://images.pexels.com/photos/1105666/pexels-photo-1105666.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=1400')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div aria-hidden className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#1B1614] via-[#1B1614]/70 to-transparent" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur border border-white/20 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">
                <Ticket size={12} /> Events
              </div>
              <h1 className="mt-4 text-4xl md:text-6xl font-black tracking-tight leading-[1.02]">
                Live music, block parties<span className="block text-[#F0C9C4]">and pop-ups near you.</span>
              </h1>
              <p className="mt-4 text-[15px] md:text-base leading-relaxed text-white/70 max-w-xl">
                Concerts, DJ nights, open mics and community meetups from bands on Servrevu.
                Free RSVP or paid tickets with QR check-in.
              </p>
            </div>
            {isBandOwner && (
              <Link to="/concerts/new" data-testid="events-create-cta">
                <Button className="rounded-[35px] shadow-sm hover:shadow-md bg-white text-[#1B1614] hover:bg-[#F5EEE8] font-semibold">
                  <PlusCircle size={14} className="mr-1.5" /> Create event
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 md:px-10 py-12">
        {/* Concerts grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="events-loading">
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i} className="rounded-md overflow-hidden">
                <div className="aspect-[16/9] bg-gray-100 animate-pulse" />
                <div className="p-4 space-y-2">
                  <div className="h-4 bg-gray-100 rounded animate-pulse w-2/3" />
                  <div className="h-3 bg-gray-100 rounded animate-pulse w-3/4" />
                </div>
              </Card>
            ))}
          </div>
        ) : concerts.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="events-grid">
            {concerts.map((c) => (
              <Link key={c.id} to={`/concerts/${c.id}`} className="group" data-testid={`event-card-${c.id}`}>
                <Card className="rounded-md overflow-hidden hover:border-[#B31E1E]/40 transition-colors h-full">
                  <div className="relative aspect-[16/9] bg-gradient-to-br from-[#1B1614] to-[#5C4E48] overflow-hidden">
                    {c.cover_image_url ? (
                      <img src={c.cover_image_url} alt={c.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white"><Ticket size={40} /></div>
                    )}
                    {c.is_free ? (
                      <Badge className="absolute top-3 right-3 rounded-sm bg-emerald-500 text-white hover:bg-emerald-500 text-[10px]">Free</Badge>
                    ) : (
                      <Badge className="absolute top-3 right-3 rounded-sm bg-[#B31E1E] text-white hover:bg-[#B31E1E] text-[10px]">Ticketed</Badge>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="text-[10px] uppercase tracking-wider font-mono text-[#B31E1E]">{c.artist_stage_name}</p>
                    <h3 className="mt-1 font-semibold text-[#1B1614] line-clamp-2">{c.title}</h3>
                    <div className="mt-2 space-y-1 text-xs text-[#5C4E48]">
                      <p className="inline-flex items-center gap-1"><Calendar size={11} /> {formatWhen(c.starts_at)}</p>
                      {c.venue_name && <p className="inline-flex items-center gap-1"><MapPin size={11} /> {c.venue_name}{c.city ? `, ${c.city}` : ""}</p>}
                      {(c.rsvp_count > 0 || c.ticket_count > 0) && (
                        <p className="inline-flex items-center gap-1 text-[#B31E1E]"><Users size={11} /> {c.rsvp_count + c.ticket_count} going</p>
                      )}
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <Card className="rounded-2xl border border-dashed border-[#EAE3DE] p-8 md:p-10 text-center bg-[#FAF5F2]" data-testid="events-empty-state">
            <div className="w-14 h-14 rounded-2xl bg-[#B31E1E] flex items-center justify-center mx-auto text-white">
              <Calendar size={24} />
            </div>
            <h2 className="mt-5 text-2xl md:text-3xl font-black tracking-tight text-[#1B1614]">
              No events yet — {isBandOwner ? "yours could be the first." : "check back soon."}
            </h2>
            <p className="mt-3 text-[15px] leading-relaxed text-[#5C4E48] max-w-xl mx-auto">
              Bands and creators can post free RSVP events or ticketed shows with QR check-in.
              {isBandOwner ? " You're already set up — create your first event now." : " Browse the featured bands below and DM them to book a gig."}
            </p>
            <div className="mt-5 flex items-center justify-center gap-3 flex-wrap">
              {isBandOwner ? (
                <Link to="/concerts/new">
                  <Button className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]" data-testid="events-empty-create">
                    <PlusCircle size={14} className="mr-1.5" /> Create event
                  </Button>
                </Link>
              ) : (
                <Link to="/artists">
                  <Button className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]" data-testid="events-browse-bands">
                    <Music size={14} className="mr-1.5" /> Browse bands
                  </Button>
                </Link>
              )}
              <Link to="/marketplace">
                <Button variant="outline" className="rounded-[35px] shadow-sm hover:shadow-md" data-testid="events-marketplace">Browse marketplace</Button>
              </Link>
            </div>
          </Card>
        )}

        {/* Featured bands rail */}
        <div className="mt-12">
          <div className="flex items-end justify-between mb-4">
            <div>
              <p className="text-[11px] uppercase tracking-wider font-mono text-[#B31E1E]">Book a live act</p>
              <h3 className="mt-1 text-2xl font-black tracking-tight text-[#1B1614]">Featured local bands</h3>
            </div>
            <Link to="/artists" className="text-sm text-[#B31E1E] hover:underline" data-testid="events-see-all-bands">See all &rarr;</Link>
          </div>

          {featuredBands.length === 0 ? null : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="events-bands-grid">
              {featuredBands.slice(0, 8).map((b) => (
                <Link key={b.id} to={`/artists/${b.id}`} className="group" data-testid={`events-band-card-${b.id}`}>
                  <Card className="rounded-md overflow-hidden hover:border-[#B31E1E]/40 transition-colors h-full">
                    <div className="aspect-square bg-gradient-to-br from-[#1B1614] to-[#5C4E48] flex items-center justify-center text-white overflow-hidden">
                      {b.cover_image_url ? (
                        <img src={b.cover_image_url} alt={b.stage_name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <Music size={40} className="group-hover:scale-110 transition-transform duration-500" />
                      )}
                    </div>
                    <div className="p-3">
                      <div className="font-medium text-gray-900 truncate">{b.stage_name}</div>
                      {b.track_count > 0 && (
                        <div className="text-[11px] text-gray-500 mt-1 flex items-center gap-1">
                          <Users size={10} /> {b.track_count} track{b.track_count === 1 ? "" : "s"}
                        </div>
                      )}
                    </div>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
